"use client";

import { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import Button from "../../../components/Button";
import overlayStyles from "../../../css/components/overlaymodal-animations.module.css";
import styles from "../css/patientSignature.module.css";
import Footer from "../../../components/footer";

export default function PatientSignatureModal({
    isOpen,
    onClose,
    onConfirm,
    initialSignature = null,
    title = "Signature Required",
    totals = { amount: "", vat: "", total: "" },
}) {
    const sigRef = useRef();
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!sigRef.current) return;

        if (!isOpen) {
            // When modal closes, clear the canvas so we don't retain an old drawing
            try {
                sigRef.current.clear();
            } catch (_) {
                // ignore
            }
            // Reset the guard too — otherwise reopening the modal after a failed/aborted
            // confirm would leave Confirm permanently disabled.
            setSubmitting(false);
            return;
        }

        if (initialSignature) {
            try {
                // Clear then draw the saved signature only when the modal is opened,
                // so clicking Confirm (which updates parent state) does not re-draw
                // the image on top of the existing canvas and cause a duplicated view.
                sigRef.current.clear();
                sigRef.current.fromDataURL(initialSignature);
            } catch (_) {
                // ignore invalid data URL
            }
        } else {
            // No initial signature, ensure we start with a blank canvas
            try {
                sigRef.current.clear();
            } catch (_) {
                // ignore
            }
        }
    }, [isOpen]);

    const handleClear = (e) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        sigRef.current?.clear();
    };

    const handleConfirm = async (e) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        // Guard against double-click/double-tap: onConfirm creates the prescription
        // (and, for medical aid, submits the MediKredit claim) — without this, two
        // rapid clicks before the modal closes would create two separate prescriptions,
        // each getting its own accepted claim against the same script.
        if (submitting) return;
        if (!sigRef.current || sigRef.current.isEmpty()) {
            alert("Please provide a signature before continuing.");
            return;
        }
        const dataUrl = sigRef.current.getCanvas().toDataURL("image/png");
        setSubmitting(true);
        try {
            await onConfirm?.(dataUrl);
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className={`${overlayStyles.modalOverlay} ${overlayStyles.show}`}>
            <div className={`${overlayStyles.modalContent} ${styles.modalContent}`}>
                <button className={overlayStyles.closeButton} onClick={onClose} disabled={submitting}>
                    ×
                </button>

                <h2 className={styles.title}>{title}</h2>

                {/* Information/acknowledgement box */}
                <section className={styles.infoBox}>
                    <div className={styles.infoGrid}>
                        <div className={styles.mainLeft}>
                            <p className={styles.boldLine}>
                                I, the member, certify that I am liable for the above amount until final settlement by the medical aid, and that settlement by the medical aid will discharge me from liability.
                            </p>
                        </div>
                        <div className={styles.mainRight}>
                            <p className={styles.boldLine}>
                                I have received the medicine referred to in this prescription and am aware of its use.
                            </p>
                        </div>
                        <div className={styles.smallNotes}>
                            <div>The member remains liable for any charges not paid by the medical aid.</div>
                            <div>The medicine was supplied in good faith.</div>
                            <div>Keep medicine in original packaging.</div>
                        </div>
                        <div className={styles.infoRight}>
                            <div className={styles.amountRow}>
                                {/* <span className={styles.label}>Amount:</span> */}
                                <span className={styles.value}>{totals.amount}</span>
                            </div>
                            <div className={styles.amountRow}>
                                {/* <span className={styles.label}>+VAT:</span> */}
                                <span className={styles.value}>{totals.vat}</span>
                            </div>
                            <div className={`${styles.amountRow} ${styles.totalRow}`}>
                                <span className={styles.label}>Total excl. VAT:</span>
                                <span className={styles.value}>{totals.total}</span>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Signature box */}
                <div className={styles.sigWrap}>
                    <div className={styles.sigBox}>
                        <SignatureCanvas
                            ref={sigRef}
                            penColor="black"
                            canvasProps={{ className: styles.canvas, width: 600, height: 220 }}
                        />
                    </div>
                    <div className={styles.hintRow}>
                        <div className={styles.hint}>Click and drag to sign</div>
                        <div className={styles.clearSlot}>
                            <Button type="button" onClick={handleClear} variant="secondary" disabled={submitting}>
                                Clear
                            </Button>
                        </div>
                    </div>
                </div>

                <div className={styles.actionBar}>
                    <Button
                        className={styles.buttonConfirm}
                        type="button"
                        onClick={handleConfirm}
                        variant="primary"
                        disabled={submitting}
                    >
                        {submitting ? "Please wait…" : "Confirm"}
                    </Button>
                </div>
            </div>
            <Footer />
        </div>
    );
}

